/**
 * The headline count under a Library or folder title, and the rule that keeps it
 * still.
 *
 * Pure so both halves can be tested without React: the component owns only the
 * ref that carries the pin between renders.
 */

/** A total pinned to the query it was read from. */
export type PinnedTotal = { key: string; value: number };

/**
 * How many rows a folder view lists.
 *
 * `total` is the artifact fetch's total and leaves chats out of it — they are
 * excluded from every folder-contents fetch and arrive as one collapsed "Chats"
 * row however many there are. So a view that surfaces them lists one row more
 * than its total, and one that does not lists exactly its total. Same rule the
 * folder rows in the Library follow (`getLibraryItemSummary`), so a folder's
 * count reads the same whether you are looking at it from outside or standing
 * in it.
 */
export function folderViewItemCount(params: {
  total: number;
  showsChatsRow: boolean;
}): number {
  return params.total + (params.showsChatsRow ? 1 : 0);
}

/**
 * The count to render, and the pin to carry into the next render.
 *
 * Pinned per query rather than re-read from each page: "Load more" refetches
 * pages 1..N and re-reads the total from the last of them, so a number this
 * prominent was being recomputed on every scroll. The server returns the full
 * filtered size on every page, so it should land on the same value either way,
 * but it should not depend on that.
 *
 * `canPin` is the guard that makes it safe. A total may only be pinned when the
 * data it was read from answers the query being rendered. A query change — into
 * a folder, or from Agents to Datasets — takes effect a render before its fetch
 * settles, and until then the page still holds the answer to the question it
 * just stopped asking. Pinning that captured the old count under the new key,
 * and because the key never changed again, the wrong number stayed until a
 * reload: a folder of 8 rows reporting the 40 items of the Library you entered
 * it from, or a Datasets filter still showing the agent count.
 */
export function resolveDisplayedTotal(params: {
  /** Pin carried from the previous render. */
  previous: PinnedTotal | null;
  /** Identity of the current query: folder, filters, search. */
  key: string;
  /** The count this render would show. */
  count: number;
  /** Whether `count` came from loaded data belonging to this view. */
  canPin: boolean;
}): { pinned: PinnedTotal | null; total: number } {
  const { previous, key, count, canPin } = params;
  if (previous !== null && previous.key === key) {
    return { pinned: previous, total: previous.value };
  }
  if (canPin) {
    const pinned = { key, value: count };
    return { pinned, total: count };
  }
  // Nothing to pin yet: show what the render has and try again next time. The
  // stale value is on screen for a beat, the way the previous view's rows are,
  // rather than being frozen in place.
  return { pinned: previous, total: count };
}

/**
 * The question a fetch answered: which folder, under which filters.
 *
 * Stamped onto the fetched data so a reader can tell whether what it holds
 * belongs to the view it is rendering. Sort and paging are left out on purpose
 * — they reorder or extend a list without changing how much is in it.
 *
 * Typed as plain strings rather than the model's param unions so the count
 * rules stay free of the model (which imports them).
 */
export type FolderContentsQuery = {
  /** The folder the fetch was for, or null for the Library root. */
  folderId: string | null;
  type: string;
  ownership: string;
  search: string;
  scope: string | null;
};

/**
 * One string for a query, so "is this the same question?" is one comparison.
 *
 * The page keys its pin off this and the data carries it, which is what makes
 * the two comparable at all.
 */
export function folderContentsQueryKey(query: FolderContentsQuery): string {
  return [
    query.folderId ?? 'root',
    query.type,
    query.ownership,
    query.search,
    query.scope ?? '',
  ].join('|');
}
